import { useState, useEffect, useMemo } from 'react';
import { getRawData } from '../../services/front_api';
import BarChart from './BarChart';
import BarLineChart from './BarLineChart';
import IndicatorChart from './IndicatorChart';
import LineChart from './LineChart';
import PieChart from './PieChart';
import StackedColumnChart from './StackedColumnChart';
import { hasRole, rolesMap } from '../../services/auth';
import FilterChooser from '../filters/FilterChooser';
import LocalFiltersModal from '../filters/LocalFiltersModal';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';

const getFirstDefined = (...values) => values.find((value) => value !== undefined && value !== null && value !== '');

const DEFAULT_CHART_SCALE = 0.5;

const normalizeFontSize = (value) => {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }

    const numericValue = Number(value);
    if (Number.isFinite(numericValue) && numericValue > 0) {
        return numericValue;
    }

    if (typeof value !== 'string') {
        return undefined;
    }

    const trimmedValue = value.trim();
    return /^\d+(\.\d+)?(px|em|rem|%)$/.test(trimmedValue) ? trimmedValue : undefined;
};

const normalizeScale = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : undefined;
};

const splitChartParams = (params) => {
    if (!params || typeof params !== 'object' || Array.isArray(params)) {
        return {
            queryParams: {},
            chartOptions: {}
        };
    }

    const {
        chartOptions,
        titleFontSize,
        legendFontSize,
        scale,
        fontScale,
        font_scale,
        axisScale,
        axis_scale,
        title_font_size,
        legend_font_size,
        ...queryParams
    } = params;

    const chartOptionsObject = chartOptions && typeof chartOptions === 'object' && !Array.isArray(chartOptions)
        ? chartOptions
        : {};

    return {
        queryParams,
        chartOptions: {
            ...chartOptionsObject,
            scale: normalizeScale(getFirstDefined(scale, fontScale, font_scale, axisScale, axis_scale, chartOptionsObject.scale, chartOptionsObject.fontScale, chartOptionsObject.font_scale, chartOptionsObject.axisScale, chartOptionsObject.axis_scale)),
            titleFontSize: normalizeFontSize(getFirstDefined(titleFontSize, title_font_size, chartOptionsObject.titleFontSize, chartOptionsObject.title_font_size)),
            legendFontSize: normalizeFontSize(getFirstDefined(legendFontSize, legend_font_size, chartOptionsObject.legendFontSize, chartOptionsObject.legend_font_size))
        }
    };
};

function chartChooser(view, title, data, scale, fontSizes, chartOptions) {
    switch (view) {
        case 'indic':
        case 'indicator':
        case 'kpi':
            return <IndicatorChart title={title} data={data} scale={scale} titleFontSize={fontSizes.titleFontSize} options={chartOptions} />;
        case 'bar':
            return <BarChart title={title} data={data} scale={scale} {...fontSizes} />;
        case 'line':
            return <LineChart title={title} data={data} scale={scale} {...fontSizes} />;
        case 'barLine':
        case 'bar-line':
        case 'bar_line':
        case 'mixed':
        case 'combo':
            return <BarLineChart title={title} data={data} scale={scale} {...fontSizes} />;
        case 'pie':
            return <PieChart title={title} data={data} scale={scale} {...fontSizes} />;
        case 'stackedColumn':
        case 'stacked-column':
        case 'stacked_column':
        case 'stacked':
            return <StackedColumnChart title={title} data={data} scale={scale} {...fontSizes} />;
        default:
            return <div>Graphique non supporté</div>;
    }
}

const ChartBuilder = ({ app, view, query, params, scale, titleFontSize, legendFontSize, category = 'chart' }) => {
    const [data, setData] = useState(null);
    const [title, setTitle] = useState(null);
    const [debugresponse, setDebugresponse] = useState(null);
    const [debugurl, setDebugurl] = useState(null);
    const [debugbody, setDebugbody] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showFiltersModal, setShowFiltersModal] = useState(false);
    const [activeApp, setActiveApp] = useState(app || localStorage.getItem('active_app') || '');
    const [refreshCount, setRefreshCount] = useState(0);

    const { queryParams, chartOptions } = useMemo(() => splitChartParams(params), [params]);
    const queryParamsKey = useMemo(() => JSON.stringify(queryParams), [queryParams]);
    const fontSizes = {
        titleFontSize: normalizeFontSize(getFirstDefined(titleFontSize, chartOptions.titleFontSize)),
        legendFontSize: normalizeFontSize(getFirstDefined(legendFontSize, chartOptions.legendFontSize))
    };
    const resolvedScale = normalizeScale(getFirstDefined(scale, chartOptions.scale)) ?? DEFAULT_CHART_SCALE;

    useEffect(() => {
        try {
            setIsAdmin(hasRole(rolesMap.admin));
        } catch (e) {
            setIsAdmin(false);
        }
    }, []);

    useEffect(() => {
        setActiveApp(app || localStorage.getItem('active_app') || '');
    }, [app]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [response, url, body] = await getRawData(activeApp, query, category, queryParams);
                setDebugresponse(response);
                setDebugurl(url);
                setDebugbody(body);
                const { data, title } = response;
                setData(data);
                setTitle(title);
                if (!app) {
                    const currentApp = localStorage.getItem('active_app') || '';
                    if (currentApp !== activeApp) {
                        setActiveApp(currentApp);
                    }
                }
            } catch (err) {
                setError(err);
            } finally {
                setLoading(false);
                setError(null);
            }
        };

        fetchData();
    }, [query, activeApp, refreshCount, queryParamsKey, category, app]);

    if (loading) return <div>Chargement...</div>;
    if (error) return <div>Erreur : {error.message}</div>;

    // Convertir le scale en pourcentage pour les composants de graphique.
    const scalePercent = Math.round(resolvedScale * 100);
    const resolvedView = view || (category === 'indic' ? 'indic' : undefined);
    const chart = chartChooser(resolvedView, title, data, scalePercent, fontSizes, chartOptions);

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text ?? '');
            // feedback minimal : console (ou remplacer par un toast)
            console.log('copied');
        } catch (e) {
            console.error('copy failed', e);
        }
    };

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 5, display: 'flex', gap: 8 }}>
                <button
                    onClick={() => setShowFiltersModal(true)}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.16)';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                        e.currentTarget.style.background = 'var(--bg-menu)';
                        e.currentTarget.style.color = 'var(--accent-hover)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.background = 'var(--bg-content)';
                        e.currentTarget.style.color = 'var(--accent)';
                    }}
                    title="Filtres locaux"
                    aria-label="Filtres locaux"
                    style={{
                        width: 32,
                        height: 32,
                        padding: 0,
                        cursor: 'pointer',
                        borderRadius: 4,
                        border: '1px solid rgba(0,0,0,0.1)',
                        background: 'var(--bg-content)',
                        color: 'var(--accent)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'box-shadow 0.18s ease, transform 0.18s ease, background-color 0.18s ease',
                    }}
                >
                    <FilterAltOutlinedIcon fontSize="small" />
                </button>
                {isAdmin && (
                    <button
                        onClick={() => setShowModal(true)}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.16)';
                            e.currentTarget.style.transform = 'translateY(-1px)';
                            e.currentTarget.style.background = 'var(--bg-menu)';
                            e.currentTarget.style.color = 'var(--accent-hover)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.boxShadow = 'none';
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.background = 'var(--bg-content)';
                            e.currentTarget.style.color = 'var(--accent)';
                        }}
                        title="Debug Query"
                        aria-label="Debug Query"
                        style={{
                            width: 32,
                            height: 32,
                            padding: 0,
                            cursor: 'pointer',
                            borderRadius: 4,
                            border: '1px solid rgba(0,0,0,0.1)',
                            background: 'var(--bg-content)',
                            color: 'var(--accent)',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'box-shadow 0.18s ease, transform 0.18s ease, background-color 0.18s ease',
                        }}
                    >
                        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="8" />
                            <path d="M12 8v5" />
                            <circle cx="12" cy="16.5" r="0.8" fill="currentColor" stroke="none" />
                        </svg>
                    </button>
                )}
            </div>

            {chart}

            {showModal && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(0,0,0,0.55)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999
                    }}
                    onClick={() => setShowModal(false)}
                >
                    <div
                        role="dialog"
                        aria-modal="true"
                        style={{
                            width: '92%',
                            maxWidth: 1000,
                            maxHeight: '90vh',
                            background: 'var(--bg-content)',
                            color: 'var(--text-color)',
                            borderRadius: 8,
                            padding: 0,
                            boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #eee', background: 'var(--bg-modal-header)', color: 'var(--text-color)' }}>
                            <div>
                                <strong style={{ fontSize: 16 }}>API Call details</strong>
                                <div style={{ fontSize: 12, color: '#666' }}>{title || '—'}</div>
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <button onClick={() => setShowModal(false)} style={{ padding: '6px 10px', cursor: 'pointer' }}>
                                    Fermer
                                </button>
                            </div>
                        </div>

                        <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'minmax(220px, 45%) 1fr', gap: 12, overflow: 'auto' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <strong>Request URL</strong>
                                    <button onClick={() => copyToClipboard(debugurl)} style={{ fontSize: 12, padding: '4px 8px' }}>Copier</button>
                                </div>
                                <pre style={{ margin: 0, padding: 10, background: 'var(--bg-menu)', color: 'var(--text-color)', borderRadius: 4, overflow: 'auto', wordBreak: 'break-all', maxHeight: 220 }}>
                                    {debugurl || '—'}
                                </pre>

                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                                    <strong>Request Body</strong>
                                    <button onClick={() => copyToClipboard(JSON.stringify(debugbody || {}, null, 2))} style={{ fontSize: 12, padding: '4px 8px' }}>Copier</button>
                                </div>
                                <pre style={{ margin: 0, padding: 10, background: 'var(--bg-menu)', color: 'var(--text-color)', borderRadius: 4, overflow: 'auto', wordBreak: 'break-all', maxHeight: 220 }}>
                                    {debugbody ? JSON.stringify(debugbody, null, 2) : '—'}
                                </pre>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <strong>Response</strong>
                                    <button onClick={() => copyToClipboard(JSON.stringify(debugresponse || {}, null, 2))} style={{ fontSize: 12, padding: '4px 8px' }}>Copier</button>
                                </div>
                                <div style={{ flex: 1, overflow: 'auto', background: '#fff', borderRadius: 4, border: '1px solid #eee', padding: 10 }}>
                                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                        {debugresponse ? JSON.stringify(debugresponse, null, 2) : '—'}
                                    </pre>
                                </div>

                                <div style={{ marginTop: 8 }}>
                                    <strong>Notes</strong>
                                    <div style={{ fontSize: 13, color: '#555', marginTop: 6 }}>
                                        Les informations affichées sont destinées au débogage. Ne partagez pas les données sensibles.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showFiltersModal && (
                <LocalFiltersModal
                    description="Configurez les filtres locaux pour ce graphique"
                    onClose={() => setShowFiltersModal(false)}
                    onValidate={() => {
                        setShowFiltersModal(false);
                        setRefreshCount((c) => c + 1);
                    }}
                >
                    <FilterChooser query={query} category={category} />
                </LocalFiltersModal>
            )}
        </div>
    );
};

export default ChartBuilder;
